// wav encoding handled by exporters.js

const CATEGORIES = ["drum", "bass", "guitar", "brass", "strings", "synth", "lead", "voice_lead", "voice_choir", "other"];

// Display labels (FR) + whether the category is a vocal
export const CATEGORY_LABELS = {
  drum: "Batterie", bass: "Basse", guitar: "Guitare", brass: "Cuivres",
  strings: "Cordes", synth: "Synthé / Nappe", lead: "Lead (instru)",
  voice_lead: "Voix lead", voice_choir: "Voix refrain / Chœur", other: "Autre",
};
export const isVocalCategory = (c) => c === "voice_lead" || c === "voice_choir";
const NOTE_NAMES = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];

// Krumhansl-Schmuckler key profiles
const MAJOR_PROFILE = [6.35, 2.23, 3.48, 2.33, 4.38, 4.09, 2.52, 5.19, 2.39, 3.66, 2.29, 2.88];
const MINOR_PROFILE = [6.33, 2.68, 3.52, 5.38, 2.60, 3.53, 2.54, 4.75, 3.98, 2.69, 3.34, 3.17];

function correlate(a, b) {
  const n = a.length;
  const ma = a.reduce((x, y) => x + y, 0) / n;
  const mb = b.reduce((x, y) => x + y, 0) / n;
  let num = 0, da = 0, db = 0;
  for (let i = 0; i < n; i++) {
    num += (a[i] - ma) * (b[i] - mb);
    da += (a[i] - ma) ** 2;
    db += (b[i] - mb) ** 2;
  }
  return num / (Math.sqrt(da * db) || 1);
}

// Goertzel magnitude at a given frequency
function goertzel(data, sampleRate, freq, start, len) {
  const k = (freq / sampleRate) * len;
  const w = (2 * Math.PI * k) / len;
  const cw = Math.cos(w);
  const coeff = 2 * cw;
  let s0 = 0, s1 = 0, s2 = 0;
  for (let i = 0; i < len; i++) {
    s0 = data[start + i] + coeff * s1 - s2;
    s2 = s1;
    s1 = s0;
  }
  return Math.sqrt(s1 * s1 + s2 * s2 - coeff * s1 * s2);
}

// Iterative radix-2 FFT (in-place)
function fft(re, im) {
  const n = re.length;
  for (let i = 1, j = 0; i < n; i++) {
    let bit = n >> 1;
    for (; j & bit; bit >>= 1) j ^= bit;
    j ^= bit;
    if (i < j) { const tr = re[i]; re[i] = re[j]; re[j] = tr; const ti = im[i]; im[i] = im[j]; im[j] = ti; }
  }
  for (let len = 2; len <= n; len <<= 1) {
    const ang = -2 * Math.PI / len, wr = Math.cos(ang), wi = Math.sin(ang);
    for (let i = 0; i < n; i += len) {
      let cr = 1, ci = 0;
      for (let k = 0; k < len >> 1; k++) {
        const idx = i + k, jdx = i + k + (len >> 1);
        const br = re[jdx] * cr - im[jdx] * ci;
        const bi = re[jdx] * ci + im[jdx] * cr;
        re[jdx] = re[idx] - br; im[jdx] = im[idx] - bi;
        re[idx] += br; im[idx] += bi;
        const ncr = cr * wr - ci * wi; ci = cr * wi + ci * wr; cr = ncr;
      }
    }
  }
}

function correlation(a, b, len) {
  let sa = 0, sb = 0, saa = 0, sbb = 0, sab = 0;
  for (let i = 0; i < len; i++) { sa += a[i]; sb += b[i]; saa += a[i] * a[i]; sbb += b[i] * b[i]; sab += a[i] * b[i]; }
  const cov = sab / len - (sa / len) * (sb / len);
  const va = saa / len - (sa / len) ** 2, vb = sbb / len - (sb / len) ** 2;
  return cov / (Math.sqrt(va * vb) || 1e-9);
}

/**
 * Core analysis on raw PCM — FFT-based spectral features + real vocal detection.
 * Distinguishes instruments vs lead vocal vs choir/refrain vocal.
 */
export function analyzePCM(data, sr, dataR = null) {
  // peak / rms / crest
  let peak = 0, sumSq = 0;
  for (let i = 0; i < data.length; i++) { const v = Math.abs(data[i]); if (v > peak) peak = v; sumSq += data[i] * data[i]; }
  const rms = Math.sqrt(sumSq / data.length) || 1e-6;
  const crest = 20 * Math.log10((peak || 1e-6) / rms);

  const N = 2048, half = N >> 1;
  const win = new Float32Array(N);
  for (let i = 0; i < N; i++) win[i] = 0.5 - 0.5 * Math.cos((2 * Math.PI * i) / (N - 1));

  const bands = { sub: 0, low: 0, mid: 0, pres: 0, high: 0, air: 0 };
  const chroma = new Array(12).fill(0);
  let centroidSum = 0, fluxSum = 0, frames = 0, voicedFrames = 0, polySum = 0, flatSum = 0, f0Sum = 0;
  const pitches = [];
  let prevMag = null;
  const limit = Math.min(data.length - N, sr * 20);
  const hop = N * 2;

  for (let start = 0; start <= limit; start += hop) {
    const re = new Float32Array(N), im = new Float32Array(N);
    for (let i = 0; i < N; i++) re[i] = data[start + i] * win[i];
    fft(re, im);
    const mag = new Float32Array(half);
    let tot = 0, cen = 0;
    const fchroma = new Array(12).fill(0);
    for (let k = 1; k < half; k++) {
      const m = Math.hypot(re[k], im[k]); mag[k] = m; const f = (k * sr) / N;
      tot += m; cen += f * m;
      if (f < 80) bands.sub += m; else if (f < 250) bands.low += m;
      else if (f < 2000) bands.mid += m; else if (f < 4000) bands.pres += m;
      else if (f < 8000) bands.high += m; else bands.air += m;
      if (f >= 55 && f < 2000) { const pc = ((Math.round(69 + 12 * Math.log2(f / 440)) % 12) + 12) % 12; chroma[pc] += m; fchroma[pc] += m; }
    }
    if (tot > 0) centroidSum += cen / tot;
    if (prevMag) { let fl = 0; for (let k = 1; k < half; k++) { const d = mag[k] - prevMag[k]; if (d > 0) fl += d; } fluxSum += fl / (tot || 1); }
    prevMag = mag;
    // spectral flatness (noise vs tonal): geo mean / arith mean over 100..8000 Hz
    { let logSum = 0, linSum = 0, cnt = 0;
      const kа = Math.max(1, Math.floor((100 * N) / sr)), kb = Math.floor((8000 * N) / sr);
      for (let k = kа; k < Math.min(kb, half); k++) { const m = mag[k] + 1e-9; logSum += Math.log(m); linSum += m; cnt++; }
      if (cnt) flatSum += Math.exp(logSum / cnt) / (linSum / cnt); }
    // fundamentals in 80..500 Hz: count distinct peaks (mono vs polyphonic)
    const k1 = Math.max(1, Math.floor((80 * N) / sr)), k2 = Math.floor((350 * N) / sr);
    let pk = 0, pf = 0, bandMax = 0;
    for (let k = k1; k <= k2; k++) if (mag[k] > bandMax) bandMax = mag[k];
    let f0cnt = 0;
    for (let k = k1 + 1; k < k2; k++) {
      if (mag[k] > 0.3 * bandMax && mag[k] >= mag[k - 1] && mag[k] > mag[k + 1]) f0cnt++;
      if (mag[k] > pk) { pk = mag[k]; pf = (k * sr) / N; }
    }
    f0Sum += f0cnt;
    const avg = tot / half;
    if (pf > 0 && pk > 4 * avg) { pitches.push(pf); voicedFrames++; }
    const fmax = Math.max(...fchroma) || 1;
    polySum += fchroma.filter((v) => v > 0.45 * fmax).length;
    frames++;
  }
  frames = frames || 1;
  const total = bands.sub + bands.low + bands.mid + bands.pres + bands.high + bands.air || 1;
  const subR = bands.sub / total, lowR = bands.low / total, midR = bands.mid / total;
  const presR = bands.pres / total, highR = bands.high / total, airR = bands.air / total;
  const vocalBandR = midR + presR;            // 250..4000 Hz concentration
  const centroid = centroidSum / frames;
  const flux = fluxSum / frames;
  const voicing = voicedFrames / frames;
  const polyphony = polySum / frames;
  const flatness = flatSum / frames;        // ~0 tonal, →1 noisy (percussion)
  const f0count = f0Sum / frames;           // ~1 monophonic, ≥2 polyphonic
  // pitch variance (melodic movement)
  let pitchVar = 0;
  if (pitches.length > 2) { const mu = pitches.reduce((a, b) => a + b, 0) / pitches.length; pitchVar = Math.sqrt(pitches.reduce((a, b) => a + (b - mu) ** 2, 0) / pitches.length) / mu; }
  // stereo width
  let stereoWidth = 0;
  if (dataR) { const L = Math.min(data.length, sr * 8); stereoWidth = 1 - Math.max(-1, Math.min(1, correlation(data.subarray(0, L), dataR.subarray(0, L), L))); }

  // key detection (global chroma)
  let bestScore = -2, bestKey = 0, bestMode = "major";
  for (let rot = 0; rot < 12; rot++) {
    const rotated = chroma.slice(rot).concat(chroma.slice(0, rot));
    const cMaj = correlate(rotated, MAJOR_PROFILE), cMin = correlate(rotated, MINOR_PROFILE);
    if (cMaj > bestScore) { bestScore = cMaj; bestKey = rot; bestMode = "major"; }
    if (cMin > bestScore) { bestScore = cMin; bestKey = rot; bestMode = "minor"; }
  }
  const key = `${NOTE_NAMES[bestKey]} ${bestMode === "major" ? "Maj" : "min"}`;
  const bpm = detectBPM(data, sr);

  // ---------- classification ----------
  // Robust decision tree tuned on real spectral features (crest, band ratios,
  // spectral flatness=tonality, polyphony, centroid). Order: most specific first.
  let category, vocal = false, voiceType = null;
  const lowHeavy = (subR + lowR) > 0.6;
  const highEnergy = presR + highR + airR;                               // energy >= 2kHz
  const noisyPerc = flatness > 0.6 && highEnergy > 0.4 && f0count < 2;    // hats / cymbals (very noisy, bright)
  // Vocals: energy concentrated in the 250–4000 Hz formant band, very tonal,
  // mid centroid, little low-end. Works even when the fundamental is weak.
  const isVocal = vocalBandR > 0.75 && flatness < 0.3 && centroid < 3000 && (subR + lowR) < 0.2 && f0count < 4;

  if (noisyPerc) category = "drum";                                      // hi-hats / cymbals / shakers
  else if (lowHeavy && crest > 9 && flatness > 0.2) category = "drum";   // kick (low transient hits)
  else if (lowHeavy && flatness < 0.2) category = "bass";                // sustained low tonal = bass
  else if (isVocal) {
    vocal = true;
    const layered = polyphony >= 5 || stereoWidth > 0.45;                // multiple simultaneous voices
    voiceType = layered ? "choir" : "lead";
    category = layered ? "voice_choir" : "voice_lead";
  } else if (polyphony >= 4 && flux < 0.15) category = "synth";          // pad / sustained chords
  else if (centroid > 2000 && polyphony < 3 && flatness < 0.5) category = "lead"; // bright monophonic instrumental lead
  else if (polyphony >= 3) category = "synth";                           // other polyphonic harmonic content
  else if (crest < 10 && (presR + highR) > 0.15) category = "strings";
  else if ((airR + highR) > 0.25) category = "guitar";
  else if ((presR + highR) > 0.2) category = "brass";
  else category = "other";

  return {
    bpm: Math.round(bpm), key,
    peak: +(20 * Math.log10(peak || 1e-6)).toFixed(1),
    rms: +(20 * Math.log10(rms)).toFixed(1),
    crest: +crest.toFixed(1),
    lowRatio: +(subR + lowR).toFixed(2),
    highRatio: +(presR + highR + airR).toFixed(2),
    vocalBandR: +vocalBandR.toFixed(2),
    centroid: Math.round(centroid), flux: +flux.toFixed(3),
    voicing: +voicing.toFixed(2), polyphony: +polyphony.toFixed(2),
    flatness: +flatness.toFixed(3), f0count: +f0count.toFixed(2),
    pitchVar: +pitchVar.toFixed(3), stereoWidth: +stereoWidth.toFixed(2),
    vocal, voiceType, category,
  };
}

export function analyzeBuffer(buffer) {
  const sr = buffer.sampleRate;
  const raw = buffer.getChannelData(0);
  const maxLen = Math.min(raw.length, sr * 30);
  const data = raw.subarray(0, maxLen);
  const dataR = buffer.numberOfChannels > 1 ? buffer.getChannelData(1).subarray(0, maxLen) : null;
  return analyzePCM(data, sr, dataR);
}

function detectBPM(data, sr) {
  const hop = 512;
  const frames = Math.floor(data.length / hop);
  const env = new Float32Array(frames);
  let prev = 0;
  for (let f = 0; f < frames; f++) {
    let e = 0;
    for (let i = 0; i < hop; i++) e += Math.abs(data[f * hop + i]);
    const diff = e - prev;
    env[f] = diff > 0 ? diff : 0;
    prev = e;
  }
  const envSr = sr / hop;
  const minBpm = 70, maxBpm = 180;
  const minLag = Math.floor((60 / maxBpm) * envSr);
  const maxLag = Math.floor((60 / minBpm) * envSr);
  let best = 0, bestLag = minLag;
  for (let lag = minLag; lag <= maxLag; lag++) {
    let sum = 0;
    for (let i = 0; i < frames - lag; i++) sum += env[i] * env[i + lag];
    if (sum > best) { best = sum; bestLag = lag; }
  }
  const bpm = (60 * envSr) / bestLag;
  return Math.max(minBpm, Math.min(maxBpm, bpm));
}

function classify() { return "other"; }

// ---- synthetic demo tracks so the studio works instantly ----
export async function generateDemoTracks(ctx) {
  const sr = ctx.sampleRate;
  const dur = 8;
  const bpm = 120;
  const beat = 60 / bpm;
  const make = (fill, name, category) => {
    const buf = ctx.createBuffer(1, Math.floor(sr * dur), sr);
    const d = buf.getChannelData(0);
    fill(d, sr, beat);
    return { name, category, buffer: buf };
  };
  const tracks = [];
  // kick
  tracks.push(make((d, sr, beat) => {
    for (let b = 0; b < 16; b++) {
      const start = Math.floor(b * beat * sr);
      for (let i = 0; i < sr * 0.25 && start + i < d.length; i++) {
        const t = i / sr;
        const f = 120 * Math.exp(-t * 18) + 45;
        d[start + i] += Math.sin(2 * Math.PI * f * t) * Math.exp(-t * 9) * 0.9;
      }
    }
  }, "Kick 909", "drum"));
  // hats
  tracks.push(make((d, sr, beat) => {
    for (let b = 0; b < 32; b++) {
      const start = Math.floor(b * beat * 0.5 * sr);
      for (let i = 0; i < sr * 0.05 && start + i < d.length; i++) {
        d[start + i] += (Math.random() * 2 - 1) * Math.exp(-i / (sr * 0.01)) * 0.25;
      }
    }
  }, "Hi-Hats", "drum"));
  // bass
  tracks.push(make((d, sr, beat) => {
    const notes = [55, 55, 82.4, 65.4];
    for (let b = 0; b < 16; b++) {
      const f = notes[b % 4];
      const start = Math.floor(b * beat * sr);
      for (let i = 0; i < beat * sr * 0.9 && start + i < d.length; i++) {
        const t = i / sr;
        d[start + i] += (Math.sin(2 * Math.PI * f * t) + 0.3 * Math.sin(4 * Math.PI * f * t)) * Math.exp(-t * 1.2) * 0.5;
      }
    }
  }, "Sub Bass", "bass"));
  // synth chords
  tracks.push(make((d, sr, beat) => {
    const chords = [[261.6, 329.6, 392], [220, 261.6, 329.6], [246.9, 293.7, 370], [196, 246.9, 293.7]];
    for (let b = 0; b < 4; b++) {
      const ch = chords[b % 4];
      const start = Math.floor(b * beat * 4 * sr);
      for (let i = 0; i < beat * 4 * sr && start + i < d.length; i++) {
        const t = i / sr;
        let s = 0;
        ch.forEach((f) => { s += Math.sin(2 * Math.PI * f * t); });
        d[start + i] += (s / ch.length) * 0.28 * Math.min(1, t * 4) * Math.exp(-t * 0.15);
      }
    }
  }, "Pad Synth", "synth"));
  // lead
  tracks.push(make((d, sr, beat) => {
    const mel = [523, 587, 659, 784, 659, 587, 523, 440];
    for (let b = 0; b < 16; b++) {
      const f = mel[b % mel.length];
      const start = Math.floor(b * beat * sr);
      for (let i = 0; i < beat * sr * 0.8 && start + i < d.length; i++) {
        const t = i / sr;
        const sq = Math.sign(Math.sin(2 * Math.PI * f * t));
        d[start + i] += sq * 0.18 * Math.exp(-t * 2.5);
      }
    }
  }, "Lead Pluck", "lead"));
  // Lead vocal (formant synthesis, melodic with vibrato) -> should classify as voice_lead
  tracks.push(make((d, sr, beat) => {
    const mel = [220, 246.9, 261.6, 293.7, 261.6, 246.9, 220, 196]; // A3..
    const formants = [[800, 80], [1150, 90], [2900, 120]]; // vowel "a"
    for (let b = 0; b < 16; b++) {
      const f0 = mel[b % mel.length];
      const start = Math.floor(b * beat * sr);
      const durS = beat * 0.95 * sr;
      for (let i = 0; i < durS && start + i < d.length; i++) {
        const t = i / sr;
        const vib = 1 + 0.015 * Math.sin(2 * Math.PI * 5.5 * t); // vibrato 5.5Hz
        let s = 0;
        for (let h = 1; h <= 30; h++) {
          const fh = f0 * h * vib;
          if (fh > sr / 2) break;
          let amp = 0;
          formants.forEach(([Fc, bw]) => { amp += Math.exp(-((fh - Fc) ** 2) / (2 * bw * bw)); });
          s += amp * Math.sin(2 * Math.PI * fh * t) / h;
        }
        const env = Math.min(1, t * 8) * Math.min(1, (durS / sr - t) * 8);
        d[start + i] += s * 0.12 * env;
      }
    }
  }, "Voix Lead", "voice_lead"));
  // Choir / refrain (layered harmonized vowels) -> should classify as voice_choir
  tracks.push(make((d, sr, beat) => {
    const chordRoots = [220, 261.6, 329.6]; // A3 C4 E4 (triad) sustained
    const formants = [[700, 90], [1100, 100], [2600, 130]];
    for (let b = 0; b < 4; b++) {
      const start = Math.floor(b * beat * 4 * sr);
      const durS = beat * 4 * sr;
      for (let i = 0; i < durS && start + i < d.length; i++) {
        const t = i / sr;
        let s = 0;
        chordRoots.forEach((f0, vi) => {
          const vib = 1 + 0.012 * Math.sin(2 * Math.PI * (4.5 + vi) * t);
          for (let h = 1; h <= 20; h++) {
            const fh = f0 * h * vib;
            if (fh > sr / 2) break;
            let amp = 0;
            formants.forEach(([Fc, bw]) => { amp += Math.exp(-((fh - Fc) ** 2) / (2 * bw * bw)); });
            s += amp * Math.sin(2 * Math.PI * fh * t) / h;
          }
        });
        const env = Math.min(1, t * 3) * Math.min(1, (durS / sr - t) * 3);
        d[start + i] += s * 0.05 * env;
      }
    }
  }, "Chœur Refrain", "voice_choir"));
  return tracks;
}

// ---- Mix engine graph ----
export function buildGraph(ctx, tracks, params, destination) {
  const master = ctx.createGain();
  master.gain.value = params.masterGain ?? 0.9;

  const compressor = ctx.createDynamicsCompressor();
  compressor.threshold.value = params.compThreshold ?? -18;
  compressor.ratio.value = params.compRatio ?? 3;
  compressor.attack.value = 0.005;
  compressor.release.value = 0.15;

  const limiter = ctx.createDynamicsCompressor();
  limiter.threshold.value = params.lufsTarget ? Math.max(-6, params.lufsTarget / 2) : -2;
  limiter.ratio.value = 20;
  limiter.attack.value = 0.001;
  limiter.release.value = 0.05;

  // reverb send
  const reverbGain = ctx.createGain();
  reverbGain.gain.value = params.reverb ? params.reverbAmount ?? 0.25 : 0;
  const convolver = ctx.createConvolver();
  convolver.buffer = makeImpulse(ctx, params.reverbSize ?? 2.2);

  // delay send
  const delay = ctx.createDelay(1.0);
  delay.delayTime.value = params.delayTime ?? 0.33;
  const delayFb = ctx.createGain();
  delayFb.gain.value = params.delay ? 0.35 : 0;
  const delaySend = ctx.createGain();
  delaySend.gain.value = params.delay ? params.delayAmount ?? 0.2 : 0;
  delay.connect(delayFb); delayFb.connect(delay);

  master.connect(compressor);
  compressor.connect(limiter);
  compressor.connect(reverbGain); reverbGain.connect(convolver); convolver.connect(limiter);
  compressor.connect(delaySend); delaySend.connect(delay); delay.connect(limiter);
  if (params.mono) {
    // Mono check (guide): sum L+R to reveal frequency masking / phase issues
    const splitter = ctx.createChannelSplitter(2);
    const sumL = ctx.createGain(); sumL.gain.value = 0.5;
    const sumR = ctx.createGain(); sumR.gain.value = 0.5;
    const monoBus = ctx.createGain();
    const merger = ctx.createChannelMerger(2);
    limiter.connect(splitter);
    splitter.connect(sumL, 0); splitter.connect(sumR, 1);
    sumL.connect(monoBus); sumR.connect(monoBus);
    monoBus.connect(merger, 0, 0); monoBus.connect(merger, 0, 1);
    merger.connect(destination);
  } else {
    limiter.connect(destination);
  }

  const sources = [];
  tracks.forEach((t) => {
    const src = ctx.createBufferSource();
    src.buffer = t.playBuffer || t.buffer;
    src.loop = !!params.loop;

    const hpf = ctx.createBiquadFilter();
    hpf.type = "highpass";
    hpf.frequency.value = t.hpf ? (t.hpfFreq ?? 80) : 20;

    const eqLow = ctx.createBiquadFilter();
    eqLow.type = "lowshelf"; eqLow.frequency.value = 200; eqLow.gain.value = t.eqLow ?? 0;
    const eqMid = ctx.createBiquadFilter();
    eqMid.type = "peaking"; eqMid.frequency.value = 1200; eqMid.Q.value = 1; eqMid.gain.value = t.eqMid ?? 0;
    const eqHigh = ctx.createBiquadFilter();
    eqHigh.type = "highshelf"; eqHigh.frequency.value = 5000; eqHigh.gain.value = t.eqHigh ?? 0;

    const panner = ctx.createStereoPanner();
    panner.pan.value = t.pan ?? 0;

    // per-track compressor — tightens levels & brings elements forward (broadcast-style)
    const COMP = {
      drum: { th: -18, ra: 4, at: 0.003, rl: 0.12 },
      bass: { th: -20, ra: 3.5, at: 0.012, rl: 0.18 },
      lead: { th: -22, ra: 3, at: 0.006, rl: 0.14 },
      voice_lead: { th: -24, ra: 4, at: 0.004, rl: 0.11 },
      voice_choir: { th: -22, ra: 3, at: 0.006, rl: 0.16 },
    };
    const cs = COMP[t.category] || { th: -26, ra: 2.5, at: 0.008, rl: 0.18 };
    const comp = ctx.createDynamicsCompressor();
    comp.threshold.value = cs.th; comp.ratio.value = cs.ra;
    comp.attack.value = cs.at; comp.release.value = cs.rl; comp.knee.value = 6;

    const gain = ctx.createGain();
    const solo = params.anySolo;
    const audible = solo ? t.solo : !t.mute;
    // small make-up so vocals/lead sit forward after compression
    const makeup = (t.category === "voice_lead" || t.category === "lead") ? 1.5 : (t.category === "voice_choir" ? 1 : 0);
    gain.gain.value = audible ? dbToGain((t.gain ?? 0) + makeup) : 0;

    src.connect(hpf); hpf.connect(eqLow); eqLow.connect(eqMid); eqMid.connect(eqHigh);
    eqHigh.connect(comp); comp.connect(panner); panner.connect(gain); gain.connect(master);

    // per-track meter tap (for the console VU-meters) — does not affect the audio path
    const meter = ctx.createAnalyser();
    meter.fftSize = 256;
    gain.connect(meter);

    // sidechain ducking on bass/synth from kick
    if (params.sidechain && (t.category === "bass" || t.category === "synth") && audible) {
      const bpm = params.bpm || 120;
      const beat = 60 / bpm;
      const bars = (t.buffer.duration / beat) + 1;
      const base = dbToGain(t.gain ?? 0);
      for (let b = 0; b < bars; b++) {
        const at = b * beat;
        gain.gain.setValueAtTime(base * 0.25, at);
        gain.gain.linearRampToValueAtTime(base, at + beat * 0.6);
      }
    }
    sources.push({ src, gain, panner, hpf, eqLow, eqMid, eqHigh, meter, trackId: t.id });
  });

  return { master, sources, limiter };
}

// ---- Voice & section (refrain/couplet) detection ----
export function detectVocalAndSections(tracks) {
  if (!tracks.length) return null;
  let vocal = tracks.find((t) => t.category === "voice_lead")
    || tracks.find((t) => t.category === "voice_choir")
    || tracks.find((t) => t.vocal || t.analysis?.vocal);
  if (!vocal) {
    const cands = tracks.filter((t) => !["drum", "bass"].includes(t.category));
    cands.sort((a, b) =>
      ((b.analysis?.vocalBandR || b.analysis?.highRatio || 0) + (b.analysis?.voicing || 0)) -
      ((a.analysis?.vocalBandR || a.analysis?.highRatio || 0) + (a.analysis?.voicing || 0)));
    vocal = cands[0] || tracks.find((t) => t.category === "lead") || tracks[0];
  }
  const buf = vocal.playBuffer || vocal.buffer;
  const sr = buf.sampleRate;
  const d = buf.getChannelData(0);
  const win = Math.max(1, Math.floor(sr * 1.0)); // 1s windows
  const n = Math.floor(d.length / win);
  const energy = [];
  for (let i = 0; i < n; i++) {
    let e = 0;
    for (let j = 0; j < win; j++) e += d[i * win + j] * d[i * win + j];
    energy.push(Math.sqrt(e / win));
  }
  const sorted = [...energy].sort((a, b) => a - b);
  const thr = sorted.length ? (sorted[Math.floor(sorted.length * 0.6)] || 0) : 0;
  const sections = [];
  let cur = null;
  energy.forEach((e, i) => {
    const type = e >= thr && e > 1e-4 ? "refrain" : "couplet";
    if (!cur || cur.type !== type) { if (cur) sections.push(cur); cur = { start: i, end: i + 1, type }; }
    else cur.end = i + 1;
  });
  if (cur) sections.push(cur);
  return { vocalId: vocal.id, vocalName: vocal.name, sections, totalSec: n || 1 };
}

function dbToGain(db) { return Math.pow(10, db / 20); }

// ---- Real sync: conform all tracks to a common tempo grid (bar-aligned length) ----
function padBuffer(ctx, buffer, targetSamples, shiftSamples = 0) {
  const ch = buffer.numberOfChannels;
  const out = ctx.createBuffer(ch, targetSamples, buffer.sampleRate);
  for (let c = 0; c < ch; c++) {
    const src = buffer.getChannelData(c);
    const dst = out.getChannelData(c);
    for (let i = 0; i < targetSamples; i++) {
      const si = i + shiftSamples;
      dst[i] = (si >= 0 && si < src.length) ? src[si] : 0;
    }
  }
  return out;
}

// Detect leading silence (first onset) in seconds
export function firstOnset(buffer) {
  const d = buffer.getChannelData(0);
  let peak = 1e-6;
  for (let i = 0; i < d.length; i++) { const v = Math.abs(d[i]); if (v > peak) peak = v; }
  const thr = peak * 0.06;
  for (let i = 0; i < d.length; i++) if (Math.abs(d[i]) > thr) return i / buffer.sampleRate;
  return 0;
}

/**
 * Conform every track to a shared tempo grid so the mix is tight and loops in lockstep.
 * - Project BPM detected from the drum track (fallback: median of all tracks).
 * - Common length = longest track rounded UP to whole bars.
 * - Every track buffer (and processed playBuffer) is padded to that exact length.
 * Same-length buffers => Web Audio loops stay perfectly phase-locked (fixes drift).
 */
export function conformTracks(ctx, tracks, { alignOnsets = false } = {}) {
  if (!tracks.length) return { tracks, bpm: 120, bars: 0, lengthSec: 0 };
  const sr = ctx.sampleRate;
  const drum = tracks.find((t) => t.category === "drum");
  const bpms = tracks.map((t) => t.analysis?.bpm).filter(Boolean).sort((a, b) => a - b);
  const median = bpms.length ? bpms[Math.floor(bpms.length / 2)] : 120;
  const bpm = Math.round(drum?.analysis?.bpm || median || 120);
  const beat = 60 / bpm, bar = beat * 4;

  const onsets = tracks.map((t) => (alignOnsets ? firstOnset(t.buffer) : 0));
  let maxLen = 0;
  tracks.forEach((t, i) => { const len = t.buffer.duration - onsets[i]; if (len > maxLen) maxLen = len; });
  const bars = Math.max(1, Math.ceil(maxLen / bar - 0.02));
  const lengthSec = bars * bar;
  const targetSamples = Math.round(lengthSec * sr);

  const conformed = tracks.map((t, i) => {
    const shift = Math.round(onsets[i] * sr);
    return {
      ...t,
      buffer: padBuffer(ctx, t.buffer, targetSamples, shift),
      playBuffer: t.playBuffer ? padBuffer(ctx, t.playBuffer, targetSamples, shift) : null,
      synced: true,
    };
  });
  return { tracks: conformed, bpm, bars, lengthSec };
}

// ---- Auto-Mix AI: derive per-track EQ/HPF/pan/gain/de-esser from each track's analysis ----
const RMS_TARGET = { drum: -11, bass: -13, lead: -15, synth: -16, guitar: -15, brass: -15, strings: -17, voice_lead: -13, voice_choir: -15, other: -16 };

export function autoMixTracks(tracks) {
  let spread = 0;
  return tracks.map((t) => {
    const a = t.analysis || {};
    const cat = t.category || "other";
    const target = RMS_TARGET[cat] ?? -16;
    const cur = typeof a.rms === "number" ? a.rms : -16;
    const gain = Math.max(-10, Math.min(10, +(target - cur).toFixed(1)));
    const patch = { gain, eqLow: 0, eqMid: 0, eqHigh: 0, hpf: true, hpfFreq: 80, deEsser: false, pan: 0 };
    switch (cat) {
      case "drum": patch.hpf = false; patch.hpfFreq = 30; patch.eqLow = 1.5; patch.eqHigh = 1.5; break;
      case "bass": patch.hpfFreq = 32; patch.eqLow = 1; patch.eqMid = -1.5; break;
      case "lead": patch.hpfFreq = 120; patch.eqMid = 1; patch.eqHigh = 2.5; patch.pan = 0.12; break;
      case "guitar": patch.hpfFreq = 100; patch.eqHigh = 2; patch.pan = (spread++ % 2 ? 0.32 : -0.32); break;
      case "synth": patch.hpfFreq = 100; patch.eqHigh = 1.5; patch.pan = (spread++ % 2 ? -0.28 : 0.28); break;
      case "brass": patch.hpfFreq = 120; patch.eqMid = 1.5; patch.pan = (spread++ % 2 ? 0.2 : -0.2); break;
      case "strings": patch.hpfFreq = 120; patch.eqHigh = 1.5; patch.pan = -0.22; break;
      case "voice_lead": patch.hpfFreq = 110; patch.eqMid = 1; patch.eqHigh = 2.5; patch.deEsser = true; patch.pan = 0; break;
      case "voice_choir": patch.hpfFreq = 120; patch.eqHigh = 2; patch.deEsser = true; patch.pan = (spread++ % 2 ? 0.25 : -0.25); break;
      default: patch.hpfFreq = 100;
    }
    if ((a.highRatio ?? 0) > 0.42) patch.deEsser = true;
    return { id: t.id, patch };
  });
}


function makeImpulse(ctx, seconds) {
  const rate = ctx.sampleRate;
  const len = Math.floor(rate * seconds);
  const impulse = ctx.createBuffer(2, len, rate);
  for (let c = 0; c < 2; c++) {
    const d = impulse.getChannelData(c);
    for (let i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, 2.5);
  }
  return impulse;
}

export async function renderMixBuffer(tracks, params, sampleRate) {
  const length = Math.max(...tracks.map((t) => (t.playBuffer || t.buffer).duration));
  const offline = new OfflineAudioContext(2, Math.ceil(length * sampleRate), sampleRate);
  const { sources } = buildGraph(offline, tracks, { ...params, loop: false }, offline.destination);
  sources.forEach(({ src }) => src.start(0));
  return await offline.startRendering();
}

export const QUALITY = {
  Draft: { sampleRate: 44100, bitDepth: 16, label: "MP3-like / Quick Mix" },
  Standard: { sampleRate: 44100, bitDepth: 16, label: "WAV 16-bit 44.1kHz" },
  High: { sampleRate: 48000, bitDepth: 24, label: "WAV 24-bit 48kHz" },
  Studio: { sampleRate: 48000, bitDepth: 24, label: "WAV 24-bit 48kHz LUFS" },
  Master: { sampleRate: 96000, bitDepth: 32, label: "WAV 32-bit float 96kHz" },
};

export { CATEGORIES };
